import React, { useEffect, useMemo, useState } from 'react';
import { Save, Loader2 } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useToastContext } from '../../../contexts/ToastContext';
import { useUserProfile, useUpdateUserProfile } from '../../../services/userService';
import StickySaveBar from '../../shared/product/StickySaveBar';

const AdminAccountSettings = () => {
  const { user, updateUser } = useAuth();
  const toast = useToastContext();
  const { data: profileData, isLoading } = useUserProfile();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
  });
  const [original, setOriginal] = useState(null);

  useEffect(() => {
    const profile = profileData?.data;
    const next = {
      name: profile?.name || user?.name || '',
      email: profile?.email || user?.email || '',
      phone: profile?.phone || user?.phone || '',
    };
    setFormData(next);
    setOriginal(next);
  }, [profileData, user]);

  const dirty = useMemo(() => {
    if (!original) return false;
    return (
      formData.name !== original.name ||
      formData.phone !== original.phone
    );
  }, [formData, original]);

  const updateMutation = useUpdateUserProfile({
    onSuccess: (data) => {
      if (data?.data) {
        updateUser({ ...user, ...data.data });
        const next = {
          name: data.data.name || formData.name,
          email: data.data.email || formData.email,
          phone: data.data.phone || formData.phone || '',
        };
        setFormData(next);
        setOriginal(next);
      }
      toast.success('Profile updated', 'Your account details were saved.');
    },
    onError: (error) => {
      toast.error('Update failed', error?.response?.data?.message || 'Could not update profile.');
    },
  });

  const handleSave = () => {
    if (!formData.name?.trim()) {
      toast.error('Validation', 'Name is required.');
      return;
    }
    updateMutation.mutate({
      name: formData.name.trim(),
      phone: formData.phone || null,
    });
  };

  const handleDiscard = () => {
    if (original) setFormData({ ...original });
  };

  if (isLoading) {
    return (
      <div className="py-10 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-ink" />
      </div>
    );
  }

  return (
    <div className={`space-y-5 max-w-xl ${dirty ? 'pb-20' : ''}`}>
      <StickySaveBar
        open={dirty}
        message="Unsaved account changes"
        onDiscard={handleDiscard}
        onSave={handleSave}
        saving={updateMutation.isPending}
        saveLabel="Save changes"
      />

      <div>
        <h3 className="text-base sm:text-lg font-medium text-ink mb-1">
          Account details
        </h3>
        <p className="text-sm text-ink-secondary">
          Update your name and phone. Email is tied to your login and cannot be changed here.
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-ink-secondary mb-1.5">
            Full name
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full px-3 py-1.5 text-xs border border-border bg-surface text-ink rounded-md focus:outline-none focus:ring-2 focus:ring-ink/40"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-ink-secondary mb-1.5">
            Email
          </label>
          <input
            type="email"
            value={formData.email}
            disabled
            className="w-full px-3 py-1.5 text-xs border border-border bg-surface-muted text-gray-500 rounded-md cursor-not-allowed"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-ink-secondary mb-1.5">
            Phone
          </label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className="w-full px-3 py-1.5 text-xs border border-border bg-surface text-ink rounded-md focus:outline-none focus:ring-2 focus:ring-ink/40"
          />
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={updateMutation.isPending || !dirty}
          className="inline-flex items-center justify-center gap-2 bg-ink text-white px-5 py-2.5 rounded-md hover:bg-black text-sm font-medium disabled:opacity-50"
        >
          {updateMutation.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          Save changes
        </button>
      </div>
    </div>
  );
};

export default AdminAccountSettings;
